import classNames from 'classnames'
import { graphql } from 'gatsby'
import React, { FC, useMemo } from 'react'
import {
  SanityLandingPage,
  SanityNewsListPage,
  SanityPressReleasePageConnection,
  SanityResearchProjectPage,
  SanitySmallHero,
} from '../../types/generated/graphql-types'
import { AnimatedStickySubMenu, MenuVariant } from '../components/AnimatedStickySubMenu'
import { DocumentHead } from '../components/DocumentHead'
import { Grid } from '../components/Grid'
import { LatestContent } from '../components/LatestContent'
import { Layout } from '../components/Layout'
import { ParallaxHero } from '../components/ParallaxHero'
import { BlockArea } from '../components/PortableText/BlockArea'
import { getLandingPageComponentTypes } from '../components/PortableText/components/landingPage'
import { SmallHero } from '../components/SmallHero'
import { StickySubMenuItem } from '../components/StickySubMenu'
import { VideoCampaignHero } from '../components/VideoCampaignHero'
import { VideoHero } from '../components/VideoHero'
import { useActiveMenuSection } from '../hooks/useActiveMenuSection'
import { useTranslations } from '../hooks/useTranslations'
import { mapLanguageVersions } from '../mapping/DocumentHeadMapper'
import {
  mapSanityParallaxHeroToParallaxHeroProps,
  mapSanitySmallHeroToSmallHeroProps,
} from '../mapping/HeroMapper'
import { newsItemToLatestContent } from '../mapping/PressReleaseLatestContentMapper'
import {
  sanityVideoCampaignHeroToVideoHeroProps,
  sanityVideoHeroToVideoHeroProps,
} from '../mapping/VideoMapper'
import { HeadPropsBase, PagePropsBase } from '../types/PagePropsBase'
import { getBlockAnchorList } from '../utilities/anchorList'
import { blockAnchorListToIdMap } from '../utilities/blockId'
import { notEmpty, validBlock } from '../utilities/filters'
import { isNullOrWhiteSpace } from '../utilities/string'

interface LandingPageResult {
  latestPressReleases: SanityPressReleasePageConnection
  newsListPageLink: SanityNewsListPage
  sanityLandingPage: SanityLandingPage
  sanityResearchProjectPage: SanityResearchProjectPage
  signupFormFromNewsList: SanityNewsListPage
}

const LandingPage: FC<PagePropsBase<LandingPageResult>> = ({ data }) => {
  const {
    latestPressReleases,
    newsListPageLink,
    sanityLandingPage,
    sanityResearchProjectPage,
    ...pageBaseProps
  } = data

  const {
    anchorLinkBlocks,
    anchorLinkBlocksGaTrackingId,
    contentBlocks,
    parallaxHero: heroArray,
    parentPage,
    path,
    showSubmenu,
    title,
  } = sanityLandingPage || sanityResearchProjectPage

  const translations = useTranslations()

  const latestContent = newsItemToLatestContent(latestPressReleases?.nodes ?? [])

  let heroElement: JSX.Element | undefined = undefined
  let isParallax = false

  if (heroArray?.length) {
    const hero = heroArray[0]

    if (hero && !isNullOrWhiteSpace(hero._type)) {
      switch (hero._type) {
        case 'smallHero':
          heroElement = (
            <SmallHero {...mapSanitySmallHeroToSmallHeroProps(hero as SanitySmallHero)} />
          )
          break
        case 'videoHero':
          heroElement = <VideoHero {...sanityVideoHeroToVideoHeroProps(hero)} />
          break
        case 'videoCampaignHero':
          heroElement = (
            <VideoCampaignHero
              {...sanityVideoCampaignHeroToVideoHeroProps(hero, title ?? '')}
            />
          )
          break
        default:
        case 'parallaxHero':
          isParallax = true
          heroElement = (
            <ParallaxHero
              {...mapSanityParallaxHeroToParallaxHeroProps({
                ...hero,
                parentTitle: parentPage?.page?.title ?? undefined,
                title: !isNullOrWhiteSpace(hero.title) ? hero.title : title,
              })}
            />
          )
          break
      }
    }
  }

  const filteredBlocks = useMemo(
    () => contentBlocks?.filter(validBlock) ?? [],
    [contentBlocks]
  )

  const blockAnchorList = useMemo(
    () =>
      showSubmenu
        ? getBlockAnchorList({
            anchorLinkBlocks: anchorLinkBlocks?.filter(notEmpty),
            filteredContent: filteredBlocks,
            translations,
          })
        : [],
    [anchorLinkBlocks, filteredBlocks, showSubmenu, translations]
  )

  const blockIdMap = useMemo(() => {
    if (showSubmenu) {
      return blockAnchorListToIdMap(blockAnchorList)
    }
  }, [blockAnchorList, showSubmenu])

  // TODO: Where to put share buttons on landing pages?
  // const shareProps = {
  //   subject: title ?? undefined,
  //   solidButtonBackground: true,
  // }

  // memoize blocks to allow use of intersection observer for the submenu
  const contentBlockSections = useMemo(
    () =>
      !!filteredBlocks.length ? (
        <div className="js-section-container">
          <BlockArea
            componentTypes={getLandingPageComponentTypes({
              additionalBlockProps: {
                careers: { pageHasStickyMenu: true },
                twoColumnsLeftSticky: {
                  allBlocks: filteredBlocks,
                  pageHasStickyMenu: true,
                },
                video: { videoStyle: 'rounded' },
              },
              blockIdMap,
            })}
            value={filteredBlocks}
          />
        </div>
      ) : null,
    [blockIdMap, filteredBlocks]
  )

  const progressBarOffset = 2
  const menuScrollOffset = 160 - progressBarOffset

  const { activeSectionId, sectionContainerRef } = useActiveMenuSection({
    offset: menuScrollOffset + 100,
    sectionSelector: '.js-section-container > *[id]',
  })

  const showMenu = blockAnchorList.length > 1

  const menuItems: StickySubMenuItem[] = showMenu
    ? blockAnchorList.map<StickySubMenuItem>(block => ({
        active: block.anchor === activeSectionId,
        link: `#${block.anchor}`,
        title: block.title,
      }))
    : []

  return (
    <Layout
      {...pageBaseProps}
      currentPageUrl={path ?? ''}
      pageHasStickyMenu={showMenu}
      useParallax={isParallax}
    >
      {heroElement}

      <div className="bg-site-background">
        {contentBlockSections && (
          <main className="mb-60 sm:mb-90 lg:mb-120" ref={sectionContainerRef}>
            {showMenu ? (
              <AnimatedStickySubMenu
                additionalStickyWrapperClassName={classNames('mb-60 sm:mb-90 lg:mb-120', {
                  'md:mt-30': !heroElement,
                })}
                gaTrackingId={anchorLinkBlocksGaTrackingId ?? undefined}
                items={menuItems}
                scrollOffset={menuScrollOffset}
                variant={MenuVariant.Compact}
              />
            ) : (
              // HACK: Prevent the first block's top margin collapsing by adding a flex div before the block sections
              <div className="flex mb-60 sm:mb-90 lg:mb-120"></div>
            )}

            {contentBlockSections}
          </main>
        )}

        {latestContent?.length > 0 && (
          <Grid additionalClassName="mb-60 sm:mb-90 lg:mb-120" as="section">
            <LatestContent
              allContentButtonText={translations.all ?? ''}
              allContentUrl={newsListPageLink?.path ?? undefined}
              items={latestContent}
              numItemsPerPage={5}
              title={translations.latestNews ?? ''}
              titleAs="h1"
            />
          </Grid>
        )}
      </div>
    </Layout>
  )
}

export const Head = ({ data, pageContext }: HeadPropsBase<LandingPageResult>) => {
  const { sanityLandingPage, sanityResearchProjectPage, ...pageBaseProps } = data

  const {
    anchorLinkBlocks,
    basePage,
    contentBlocks,
    indexing,
    metaDescription,
    metaImage,
    metaTitle,
    path,
    showSubmenu,
    title,
  } = sanityLandingPage || sanityResearchProjectPage

  const translations = useTranslations()

  const filteredBlocks = useMemo(
    () => contentBlocks?.filter(validBlock) ?? [],
    [contentBlocks]
  )

  const blockAnchorList = useMemo(
    () =>
      showSubmenu
        ? getBlockAnchorList({
            anchorLinkBlocks: anchorLinkBlocks?.filter(notEmpty),
            filteredContent: filteredBlocks,
            translations,
          })
        : [],
    [anchorLinkBlocks, filteredBlocks, showSubmenu, translations]
  )

  const showMenu = blockAnchorList.length > 1

  return (
    <DocumentHead
      basePage={basePage}
      description={metaDescription}
      homePage={pageBaseProps.homePage}
      indexing={indexing}
      language={pageContext.language ?? undefined}
      languageVersions={mapLanguageVersions(pageContext)}
      ogImage={metaImage}
      pageHasStickyMenu={showMenu}
      path={path ?? undefined}
      title={metaTitle ?? title}
    />
  )
}

export const query = graphql`
  query LandingPageQuery($pageId: String, $language: String) {
    sanityLandingPage(_id: { eq: $pageId }, language: { eq: $language }) {
      anchorLinkBlocks {
        block
        title
      }
      anchorLinkBlocksGaTrackingId
      basePage {
        ...BasePageFragment
      }
      contentBlocks {
        ...AnnualReviewsFragment
        ...BannerBlockFragment
        ...BannerFragment
        ...BusinessLinesFragment
        ...CareersFragment
        ...CategoryTableFragment
        ...ContactFormFragment
        ...DistributorListFragment
        ...FeaturedVideoTeaserFragment
        ...FootnotesFragment
        ...FullWidthTeaserFragment
        ...FullWidthTextFragment
        ...HorizontalLineFragment
        ...ImageGridFragment
        ...ImageTextFragment
        ...InvestmentRangeFragment
        ...InvestmentStrategiesFragment
        ...KeyDocumentsFragment
        ...KeyTermsFragment
        ...KpiBlockFragment
        ...LargeTeaserFragment
        ...PageHeadingFragment
        ...PageIntroFragment
        ...PersonContactsFragment
        ...SharesTableFragment
        ...TwoColumnsLeftStickyFragment
        ...TwoColumnTextFragment
        ...TwoTextsFragment
        ...VideoAutoplayPreviewFragment
        ...VideoBlockFragment
        ...VideoTeaserCardFragment
        ...NewsletterFormFragment
      }
      metaDescription
      metaImage {
        ...BasicImageFragment
      }
      metaTitle
      indexing
      parallaxHero {
        ...ParallaxHeroFragment
        ...SmallHeroFragment
        ...VideoHeroFragment
        ...VideoCampaignHeroFragment
      }
      parentPage {
        ...ParentPageFragment
      }
      path
      showSubmenu
      title
    }

    sanityResearchProjectPage(_id: { eq: $pageId }, language: { eq: $language }) {
      basePage {
        ...BasePageFragment
      }
      contentBlocks {
        ...BannerFragment
        ...TwoColumnTextFragment
        ...TwoColumnsLeftStickyFragment
        ...TwoTextsFragment
        ...VideoBlockFragment
      }
      indexing
      metaDescription
      metaTitle
      parallaxHero {
        ...ParallaxHeroFragment
      }
      parentPage {
        ...ParentPageFragment
      }
      path
      title
    }

    ...PageBaseFragment
    ...LatestNewsFragment
  }
`

export default LandingPage
